import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import NotificationBell from './NotificationBell';
import useIsMobile from '../../hooks/useIsMobile';
import { useAuth } from '../../context/AuthContext';

// ── Page title resolver (from route path) ────────────────────────────────────
const ROUTE_TITLES = {
  '/dashboard':           'Dashboard',
  '/analytics':           'Analytics',
  '/risk-dashboard':      'Risk Dashboard',
  '/referrals':           'Referrals',
  '/care-requests':       'Care Requests',
  '/dispatch':            'Dispatch Engine',
  '/billing':             'Billing',
  '/revenue-cycle':       'Revenue Cycle',
  '/visits':              'Visits',
  '/visit-calendar':      'Visit Calendar',
  '/recurring-visits':    'Recurring Series',
  '/care-plans':          'Care Plans',
  '/clinical':            'Clinical Reports',
  '/oasis':               'OASIS-E Assessments',
  '/prescriptions':       'Prescriptions',
  '/remote-obs':          'Remote Monitoring',
  '/billing':             'Billing',
  '/compliance':          'Compliance & Audit',
  '/rbac':                'Roles & Permissions',
  '/ss/parties':          'Party Explorer',
  '/ss/nurses':           'Nurse Registry',
  '/ss/doctors':          'Doctor Registry',
  '/ss/users':            'User Accounts',
  '/ss/roles':            'Roles',
  '/ss/permissions':      'Permissions',
  '/ss/companies':        'Organizations',
  '/ss/org-units':        'Org Units',
  '/ss/relationships':    'Relationships',
  '/ss/audit':            'Audit Log',
  '/ss/security-catalog': 'Security Catalog',
  '/ss/navigation':       'Navigation Admin',
};

function getTitle(pathname) {
  // strip query string
  const base = pathname.split('?')[0];
  return ROUTE_TITLES[base] || 'HHMS';
}

// ── Global micro-interaction + accessibility styles ───────────────────────────
const GLOBAL_CSS = `
  *, *::before, *::after { box-sizing: border-box; }

  /* Smooth transitions across interactive surfaces */
  .hhms-card {
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .hhms-card:hover {
    box-shadow: 0 16px 40px -16px rgba(45,16,32,0.18) !important;
    transform: translateY(-2px);
  }
  .hhms-card:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(45,16,32,0.07) !important;
  }

  /* Skeleton shimmer */
  @keyframes hhms-shimmer {
    0% { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
  .hhms-skeleton {
    background: linear-gradient(90deg, #EEF3F9 25%, #E3EAF2 50%, #EEF3F9 75%);
    background-size: 600px 100%;
    animation: hhms-shimmer 1.4s infinite linear;
    border-radius: 10px;
  }

  /* Focus rings for accessibility */
  button:focus-visible, a:focus-visible, input:focus-visible, select:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(123,30,60,0.18) !important;
  }

  /* Input focus ring (BDS v1.1) */
  input:focus, select:focus, textarea:focus {
    border-color: #7B1E3C !important;
    box-shadow: 0 0 0 3px rgba(123,30,60,0.18);
  }

  /* Scrollbar — thin clinical style */
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #ece3e7; border-radius: 10px; }
  ::-webkit-scrollbar-thumb:hover { background: #90a4ae; }

  /* Urgency pulse for critical badge */
  @keyframes hhms-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.55; }
  }
  .hhms-critical-pulse { animation: hhms-pulse 2s ease-in-out infinite; }

  /* Table row hover */
  .hhms-table-row { transition: background 0.1s; }
  .hhms-table-row:hover { background: #f7f3f4 !important; }

  /* Btn base */
  .hhms-btn { transition: opacity 0.15s, background 0.15s, box-shadow 0.15s, transform 0.1s; }
  .hhms-btn:hover:not(:disabled) { opacity: 0.88; }
  .hhms-btn:active:not(:disabled) { transform: scale(0.97); }
`;

// ── Session timeout warning ───────────────────────────────────────────────────
function SessionTimeoutWarning() {
  const { logout } = useAuth();
  const [remaining, setRemaining] = useState(null); // seconds left, null = not shown

  useEffect(() => {
    const check = () => {
      const token = localStorage.getItem('access_token');
      if (!token) return setRemaining(null);
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const secs = Math.round(payload.exp - Date.now() / 1000);
        setRemaining(secs <= 180 && secs > 0 ? secs : null);
        if (secs <= 0) logout();
      } catch { setRemaining(null); }
    };
    check();
    const id = setInterval(check, 10000);
    return () => clearInterval(id);
  }, [logout]);

  if (remaining === null) return null;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: '#7b1e3c', color: '#fff', borderRadius: 10,
      padding: '12px 18px', fontSize: 13, fontWeight: 600,
      boxShadow: '0 4px 20px rgba(0,0,0,0.25)', display: 'flex', gap: 14, alignItems: 'center',
    }}>
      <span>⏱ Session expires in {mins}:{String(secs).padStart(2, '0')}</span>
      <button onClick={logout} style={{
        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6,
        color: '#fff', padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
      }}>Log out now</button>
    </div>
  );
}

export default function Layout({ children }) {
  const isMobile  = useIsMobile();
  const { user }  = useAuth();
  const location  = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const pageTitle = getTitle(location.pathname);
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  useEffect(() => {
    document.body.style.overflow = (isMobile && drawerOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, drawerOpen]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f3f4', fontFamily: 'Inter, sans-serif' }}>
      <style>{GLOBAL_CSS}</style>

      {/* ── Desktop sidebar ── */}
      {!isMobile && <Sidebar />}

      {/* ── Mobile: backdrop + slide-in drawer ── */}
      {isMobile && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(45,16,32,0.65)',
            zIndex: 200, backdropFilter: 'blur(2px)',
          }}
        />
      )}
      {isMobile && (
        <div style={{
          position:  'fixed', top: 0, left: 0, bottom: 0,
          width:     260, zIndex: 201,
          transform: drawerOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition:'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <Sidebar onClose={() => setDrawerOpen(false)} />
        </div>
      )}

      {/* ── Main column ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>

        {/* ── Top bar (desktop + mobile) ── */}
        <header style={{
          display:        'flex',
          alignItems:     'center',
          gap:            12,
          background:     'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(180%) blur(10px)',
          WebkitBackdropFilter: 'saturate(180%) blur(10px)',
          padding:        isMobile ? '10px 14px' : '0 28px',
          height:         isMobile ? 'auto' : 56,
          borderBottom:   '1px solid #ECE3E7',
          position:       'sticky',
          top:            0,
          zIndex:         100,
          flexShrink:     0,
        }}>

          {/* Mobile hamburger */}
          {isMobile && (
            <button
              onClick={() => setDrawerOpen(o => !o)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 6px', borderRadius: 6, lineHeight: 1,
                display: 'flex', flexDirection: 'column', gap: 4.5,
              }}
              aria-label="Open navigation"
            >
              {[0, 1, 2].map(i => (
                <span key={i} style={{ display: 'block', width: 20, height: 2, background: '#8b7681', borderRadius: 2 }} />
              ))}
            </button>
          )}

          {/* Page title / breadcrumb */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isMobile ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span style={{ fontSize: 15, fontWeight: 800, color: '#2b1b22', letterSpacing: '-0.3px' }}>
                  HHMS
                </span>
                <NotificationBell />
              </span>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: '#90a4ae', fontWeight: 500, letterSpacing: '0.2px' }}>
                  HHMS
                </span>
                <span style={{ color: '#ece3e7', fontSize: 14 }}>›</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>
                  {pageTitle}
                </span>
              </div>
            )}
          </div>

          {/* Right side: date + user pill */}
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <NotificationBell />
              <span style={{
                fontSize: 11, color: '#8b7681', fontWeight: 500,
                background: '#F1F5FA', borderRadius: 999, padding: '4px 12px',
              }}>
                {today}
              </span>
              {/* User pill */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 10px 5px 6px',
                background: '#f6eff2', border: '1px solid #ece3e7',
                borderRadius: 20, cursor: 'default',
              }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg,#7b1e3c,#c39a4e)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: '#fff',
                }}>
                  {(user?.name || user?.username || 'A')[0].toUpperCase()}
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#1e3348', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.name || user?.username || 'User'}
                </span>
              </div>
            </div>
          )}
        </header>

        {/* ── Page content ── */}
        <main style={{
          flex:      1,
          padding:   isMobile ? '16px 14px' : '24px 28px',
          background:'#f7f3f4',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          {children}
        </main>
      </div>
      <SessionTimeoutWarning />
    </div>
  );
}
